import {detectBrowser} from "@/functions/browser";

declare global {
	interface Window {
		measurementSelectorVariables: {
			product_id: string;
			i18n: {
				[key: string]: any; /* eslint-disable-line */
			};
			fontDirUrl: string;
			modifiers;
			dimensions;
			shape: string;
			enabledShapes: string[];
			restUrl: string;
			restNonce: string;
			pricingGroup: {
				enabled_shapes: string[];
				other_settings: {
					min_drillhole_diameter: number | null;
					default_drillhole_diameter: number | null;
					enable_drillholes: boolean;
					prefill_custom_drill_holes: number[][];
					enable_cutout: boolean;
					fillet_sharp_edges: boolean;
					max_safe_cutout_percentage: number | null;
					drill_hole_presets_active: boolean|number;
					customer_choose_production_method: boolean;
				};
				shape_settings: {
					arrow: object;
					circle: object;
					dxf: object;
					egg: object;
					ellipse: object;
					"flat-oval": object;
					"half-circle": object;
					heart: object;
					hexagon: object;
					octagon: object;
					polygon: object;
					"isosceles-triangle": object;
					"right-angled-triangle": object;
					"right-angled-triangle-mirrored": object;
					"rounded-rectangle": object;
					"sloped-rectangle": object;
					"sloped-rectangle-flip-horizontal": object;
					"sloped-right-rectangle": object;
					"sloped-right-rectangle-flip-vertical": object;
					star: object;
					text: object;
					trapezoid: object;
					kite: object;
					barrel: object;
					jewel: object;
					droplet: object;
					parallelogram: object;
				};
			};
			settings: {
				standardMaterialLoss: number;
				pricePerM2IncludingTax: number;
				sheetSides: string;
				displayTexture: string;
				hasCustomTexture: boolean;
				customTexture: {
					url: string;
					width: number;
					height: number;
				};
				default_price: number;
				default_price_ex_tax: number;
			};
			shapeSettings: {
				rectangle: {
					minimumLength?: number;
					minimumWidth?: number;
				};
				default: {
					minimumDimension?: number;
				};
				maximumLength?: number;
				maximumWidth?: number;
				canUseFullPlateLength?: boolean;
				canUseFullPlateWidth?: boolean;
				area?: number;
				maximumMaterial?: {
					length: number;
					width: number;
				};
				defaultDimensions: {
					arrow: object;
					circle: object;
					dxf: object;
					egg: object;
					ellipse: object;
					"flat-oval": object;
					"half-circle": object;
					heart: object;
					hexagon: object;
					polygon: object;
					"isosceles-triangle": object;
					rectangle: object;
					"right-angled-triangle": object;
					"right-angled-triangle-mirrored": object;
					"rounded-rectangle": object;
					"sloped-rectangle": object;
					star: object;
					text: object;
					trapezoid: object;
					kite: object;
					barrel: object;
					jewel: object;
					droplet: object;
					parallelogram: object;
					'parallelogram-flip-horizontal': object;
				};
			};
			cutoutSettings: {
				minimumLength?: number;
				minimumWidth?: number;
			};
			drillHoleSettings: {
				prefilledDrillHoles: object[];
				usePreset: boolean;
				presetGroup: {
					drill_hole_max_distance: number;
					drill_hole_distance_to_edge_top_bottom: number;
					drill_hole_distance_to_edge_left_right: number;
					drill_hole_starting_point: number;
				};
			};
			measurementsConfig: object[];
			configurationName: string;
			convertConfig: boolean;
			convertCustomDrillHoles: boolean;
			currentShortLinkUrl: string;
			thickness: number;
			alternateThicknesses: object[];
			material: materialProduct;
			alternateMaterials: materialProduct[];
			loggedInUser: string;
			shop: {
				name: string;
				currency: string;
				locale: string;
			};
			html: {
				availability: string;
			};
			drawingExplainer: {
				url: string;
				title: string;
				target: string;
			} | null;
			drawingRules: {
				url: string;
				title: string;
				target: string;
			} | null;
			assets: string;
			edgeFinishingIsEnabled: string;
			sideSelectionSettings: {
				showSideSelection: boolean;
			}
		};
		extendedProductInformation: ExtendedProductInformationLayouts;
		dataLayer: {
			push: Function;
		};
		appDebug: boolean;
	}
}

import PortalVue from 'portal-vue';
import { configureCompat, createApp, h } from 'vue';
import vSelect from 'vue-select';
import V3ScrollLock from 'v3-scroll-lock';
import { FocusTrap } from 'focus-trap-vue';
import App from './App.vue';
import { store } from './store';
import { ExtendedProductInformationLayouts } from './types/extendedProductInformation';
import { materialProduct } from "./types/product";

/*
 * Before mounting hide the product delivery information that's already there.
 * Do this in JavaScript to make it available to noscript users.
 */
const productDelivery = document.querySelector<HTMLElement>('.summary > .product-delivery');
if (productDelivery) {
	productDelivery.hidden = true;
}

// Required to trigger on first load if it's a customized shape, because of preset drillholes for example
if(store.getters.exportShape === 'customized-shape') {
	store.dispatch( 'retrieveDxfDefinitionDebounced' );
}

// Required to trigger the async remote price calculation based on a getter changing.
store.watch( ( state, getters ) => getters.dxfExport || getters.activeEdgeFinish, async() => {
	await store.dispatch( 'retrieveDxfDefinitionDebounced' );
});

const startApp = async (): Promise<void> => {
	const app = createApp({
		name: 'MeasurementsSelector', // Appears in Vue devtools
		render() {
			return h(App);
		}
	});

	app.component('VSelect', vSelect);
	app.component('FocusTrap', FocusTrap);
	app.use(store);
	app.use(PortalVue);
	app.use(V3ScrollLock, {});

	// Register a global custom directive called `v-focus`
	app.directive('focus', {

		// When the bound element is mounted into the DOM...
		mounted(el) {

			// Focus the element
			el.focus();
		}
	});

	// Set global variables
	app.config.globalProperties.$i18n = window.measurementSelectorVariables.i18n;
	app.config.globalProperties.$kpsSettings = window.measurementSelectorVariables;
	app.config.globalProperties.$userBrowser = detectBrowser();

	// Make global variable available in <setup>.
	app.provide('userBrowser', detectBrowser());

	app.mount('#app-measurements-selector');
};

// Component declares "modelValue" prop, which is Vue 3 usage. Opt-in to Vue 3 behavior.
configureCompat({
	COMPONENT_V_MODEL: false,
	ATTR_FALSE_VALUE: 'suppress-warning',
	RENDER_FUNCTION: 'suppress-warning',
});

startApp();
